import { getI18n } from 'react-i18next';

import { User } from '../../app/store/types/auth.types';
import { NewPasswordForm } from '../../pages/other/Profile/types';
import { localizationMapper } from '../localization';
import { api } from './api';
import { DataSerieFilterParams, GetDataSeriesParams, StaticFilesParams } from './types';
import { NewUserForm } from '../../components/Settings/UsersTable/types';
import { FilterState } from '../../hooks/filters/types';

/**
 * Location Services
 */
export const locationService = {
  /**
   * Get locations for map display
   */
  getLocationsMap: (clientId: string) => {
    return api.get('/api/locations/map', { clientId });
  },

  /**
   * Get detailed location data
   */
  getLocationData: (locationId: string) => {
    return api.get('/api/locations/location', { locationId });
  },
};

/**
 * Static File Services
 */
export const staticFileService = {
  /**
   * Get generic static files
   */
  getStaticFiles: (staticUrl: string, params: StaticFilesParams) => {
    return api.get(`/api/staticFiles/${staticUrl}`, params);
  },

  /**
   * Get recommendations
   */
  getRecommendations: (params: StaticFilesParams) => {
    return api.get('/api/staticFiles/recommendations', params);
  },

  /**
   * Get explanations
   */
  getExplanation: (params: StaticFilesParams) => {
    return api.get('/api/staticFiles/explanation', params);
  },

  /**
   * Get legend
   */
  getLegend: (params: StaticFilesParams) => {
    return api.get('/api/staticFiles/legend', params);
  },

  /**
   * Get pollution classes
   */
  getPollutionClasses: (params: StaticFilesParams) => {
    return api.get('/api/staticFiles/pollutionClasses', params);
  },

  /**
   * Get project introduction
   */
  getProjectIntroduction: (params: StaticFilesParams) => {
    return api.get('/api/staticFiles/projectIntroduction', params);
  },
};

/**
 * App Information Services
 */
export const appService = {
  /**
   * Get application version and information
   */
  getAppInformation: () => {
    return api.get('/api/appVersion/get', {});
  },
};

/**
 * Export Services
 */
export const exportService = {
  /**
   * Get excel export of data
   */
  getExcelExport: (params: FilterState) => {
    return api.getFile('/api/exportData/get', params);
  },
};

/**
 * Client Services
 */
export const clientService = {
  /**
   * Get all clients
   */
  getAllClients: () => {
    return api.get('/api/clients/get', {});
  },
};

/**
 * User Management Services
 */
export const userService = {
  /**
   * Get current logged in user details
   */
  getCurrentUser: () => {
    return api.get('/api/Account/users/me', {});
  },

  /**
   * Change password for current user
   */
  changePassword: (params: NewPasswordForm) => {
    return api.update('/api/Account/users/changePassword/me', params);
  },

  /**
   * Change password for a specific user
   */
  changeUserPassword: (userId: string, params: NewPasswordForm) => {
    return api.update(`/api/Account/users/changeUserPassword/${userId}`, params);
  },

  /**
   * Get all users
   */
  getUsers: () => {
    return api.get('/api/Account/users/', {});
  },

  /**
   * Get specific user by ID
   */
  getUser: (userId: string) => {
    return api.get(`/api/Account/users/${userId}`, {});
  },

  /**
   * Get users for a specific client
   */
  getUsersOfClient: (clientId: string, isAdminUser: boolean) => {
    if (isAdminUser) {
      return userService.getUsers();
    }
    return api.get(`/api/Account/users/client/${clientId}`, {});
  },

  /**
   * Add a new user
   */
  addNewUser: (user: NewUserForm) => {
    return api.create('/api/Account/users/', user);
  },

  /**
   * Update existing user
   */
  updateUser: (user: User) => {
    return api.update(`/api/Account/users/${user.id}`, user);
  },

  /**
   * Delete user
   */
  deleteUser: (userId: string) => {
    return api.delete(`/api/Account/users/${userId}`);
  },
};

/**
 * Data Series Services
 */
export const dataSeriesService = {
  /**
   * Get specific data series
   */
  getDataSerie: (params: GetDataSeriesParams) => {
    return api.get('/api/dataseries/get', {
      ...params,
      culture: localizationMapper(getI18n().language),
    });
  },

  /**
   * Get all data series for a location
   */
  getAllDataSeries: (params: Partial<FilterState>) => {
    return api.get('/api/dataseries/getAll', {
      ...params,
      culture: localizationMapper(getI18n().language),
    });
  },

  /**
   * Get data for a specific data series
   */
  getDataSerieData: (params: DataSerieFilterParams) => {
    return api.get('/api/dataseries/getData', {
      ...params,
      culture: localizationMapper(getI18n().language),
    });
  },
};

/**
 * Maintenance Alert Services
 */
export const maintenanceService = {
  /**
   * Get maintenance alerts
   */
  getMaintenanceAlerts: (clientId?: string, locationId?: string) => {
    const params: Record<string, string> = {};
    if (clientId) params.clientId = clientId;
    if (locationId) params.locationId = locationId;

    return api.get('/api/maintenanceAlerts/get', params);
  },
};
